import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { OBRAS } from "../data/obras";
import type { ChapterData } from "../data/content-types";
import type { InteractableDef } from "../core/types";
import { TILE_W, TILE_H } from "../core/iso";
import * as P from "../art/props";
import { makeVisual, preloadConstructionAssets } from "../visual/SpriteFactory";
import { CharacterVisual } from "../visual/CharacterVisual";
import { parseTiledMap, type LoadedMap, type TiledMap } from "../map/tiled";
import { useRealAssets } from "../config";
import mapJson from "../map/obras-del-norte.json";

/** Mapa parseado una sola vez (función pura, sin Phaser). */
const LOADED: LoadedMap = parseTiledMap(mapJson as unknown as TiledMap);

/** Clave de textura del tileset de suelo. */
const GROUND_TEX = "ground:obras";

/**
 * Contenido de la microescena. REUTILIZA los datos del capítulo clásico
 * (hotspots, NPC, riesgos) — el "qué hace" — y solo baja los umbrales para
 * un recorrido corto. El "dónde está" viene del mapa de Tiled, no de aquí.
 * Guarda en su propia localización ("obras-micro") para no tocar el progreso
 * real de "obras".
 */
const OBRAS_MICRO: ChapterData = {
  ...OBRAS,
  id: "obras-micro",
  title: "Obras del Norte (microescena)",
  location: "obras-micro",
  interactables: [], // se ignora: las posiciones vienen de Tiled
  needTalk: 1,
  needHazards: 1,
  mission: {
    id: "obras_micro",
    title: "Obras del Norte (microescena)",
    location: "obras-micro",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con Antonio", target: "1" },
      { id: "hazards", type: "discover_hazards", label: "Detecta un riesgo", target: "1" },
      { id: "equip", type: "equip", label: "Prepárate en la taquilla", hidden: true },
    ],
  },
};

/**
 * MICROESCENA PILOTO del nuevo workflow (Tiled + sprites).
 * - posiciones desde el mapa de Tiled (editable visualmente);
 * - arte por SpriteFactory (sprite real o placeholder vectorial);
 * - lógica intacta, heredada de WalkChapter y resuelta por id/ref.
 */
export class ObrasMicroScene extends WalkChapter {
  protected chapter: ChapterData = OBRAS_MICRO;
  protected grid = LOADED.grid;
  protected spawn = LOADED.spawn;
  protected hideLabels = true;
  protected worldSigns = [
    { id: "m_casco", x: 1.4, y: 0.4 },
    { id: "w_caida", x: 5, y: 0.3 },
    { id: "w_objetos", x: 8.4, y: 0.4 },
  ];

  private map = LOADED;
  private decorObjs: Phaser.GameObjects.Container[] = [];

  constructor() {
    super("obras-micro");
  }

  // --- assets reales (si están activados; si no, placeholders) ---
  protected preloadAssets(): void {
    preloadConstructionAssets(this);
    CharacterVisual.preload(this);
    // tileset de suelo (si el mapa lo trae y los assets reales están activos)
    const gr = this.map.ground;
    if (gr && useRealAssets()) {
      this.load.spritesheet(GROUND_TEX, gr.imagePath, { frameWidth: gr.tileW, frameHeight: gr.tileH });
      this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, () => {});
    }
  }

  // --- las POSICIONES de los interactivos vienen del mapa ---
  protected getInteractables(): InteractableDef[] {
    return this.map.interactions;
  }

  // --- arte del interactivo: sprite/placeholder según su clave visual ---
  protected buildInteractableArt(def: InteractableDef): Phaser.GameObjects.GameObject[] {
    // NPC: sistema reutilizable de personaje (sprite real o placeholder)
    if (def.type === "npc") {
      const cv = new CharacterVisual(this, def.ref ?? def.id);
      cv.setDirection("SW");
      return [cv.container];
    }
    if (def.type === "door") return [this.buildExitGate()];
    if (def.visual) return [makeVisual(this, def.visual)];
    return super.buildInteractableArt(def);
  }

  /** Portón de salida de obra con cartel verde. */
  private buildExitGate(): Phaser.GameObjects.Container {
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.16); g.fillEllipse(0, 4, 66, 16); // sombra
    // postes
    g.fillStyle(0x5a5f68, 1); g.fillRect(-30, -56, 6, 56); g.fillRect(24, -56, 6, 56);
    g.fillStyle(0x3a3f47, 1); g.fillRect(-30, -56, 2, 56); g.fillRect(24, -56, 2, 56);
    // hoja del portón (malla amarilla)
    g.fillStyle(0xd8b93f, 1); g.fillRect(-27, -54, 51, 6);
    g.lineStyle(3, 0xd8b93f, 1); g.strokeRect(-27, -54, 51, 50);
    g.lineStyle(1.4, 0xb99a28, 0.9);
    for (let x = -24; x < 24; x += 9) { g.beginPath(); g.moveTo(x, -54); g.lineTo(x - 7, -4); g.strokePath(); g.beginPath(); g.moveTo(x, -4); g.lineTo(x - 7, -54); g.strokePath(); }
    // banda reflectante
    g.fillStyle(0xb23a2a, 0.85); for (let x = -25; x < 24; x += 14) g.fillRect(x, -53, 7, 4);
    // cartel de salida (verde con flecha)
    g.fillStyle(0x2f8f57, 1); g.fillRoundedRect(-16, -74, 32, 13, 2);
    g.fillStyle(0xffffff, 1); g.fillRect(-10, -69, 10, 3); g.fillTriangle(0, -72, 0, -60, 8, -66);
    return this.add.container(0, 0, [g]);
  }

  // --- colisión sólida desde la capa COLLISION de Tiled ---
  protected isBlocked(x: number, y: number): boolean {
    for (const r of this.map.collisions) {
      if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return true;
    }
    return false;
  }

  // --- fondo: calima de obra + skyline lejano (profundidad) ---
  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x9a8560);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0xcfc19d, 0xcfc19d, 0x9a8560, 0x9a8560, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.62 + over);

    // skyline lejano en la calima (parallax suave)
    const sk = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    const base = H * 0.44;
    const blds: [number, number, number][] = [[40, 120, 90], [180, 80, 60], [280, 150, 120], [450, 95, 70], [545, 130, 100], [980, 110, 85], [1100, 165, 130], [1210, 90, 60]];
    for (const [x, w, ht] of blds) {
      sk.fillStyle(0xbfb086, 0.9); sk.fillRect(x, base - ht, w, ht);
      sk.fillStyle(0xc9bc92, 0.9); sk.fillRect(x, base - ht, w, 5); // remate claro
      sk.fillStyle(0xa89a72, 0.55); // ventanas insinuadas
      for (let wy = base - ht + 12; wy < base - 8; wy += 16) for (let wx = x + 6; wx < x + w - 6; wx += 14) sk.fillRect(wx, wy, 7, 8);
    }
    // grúas torre
    const crane = (cx: number, cy: number, dir: number) => {
      sk.lineStyle(3, 0xa89880, 0.95);
      sk.beginPath(); sk.moveTo(cx, base); sk.lineTo(cx, cy); sk.strokePath();                       // mástil
      sk.beginPath(); sk.moveTo(cx - 30 * dir, cy + 6); sk.lineTo(cx + 120 * dir, cy - 2); sk.strokePath(); // pluma
      sk.beginPath(); sk.moveTo(cx, cy - 14); sk.lineTo(cx + 120 * dir, cy - 2); sk.moveTo(cx, cy - 14); sk.lineTo(cx - 30 * dir, cy + 6); sk.strokePath();
      sk.fillStyle(0xa89880, 0.95); sk.fillRect(cx + 80 * dir, cy - 2, 5, 22); // gancho/carga
    };
    crane(360, base - 150, 1);
    crane(1020, base - 165, -1);
  }

  // --- suelo: tileset real de Tiled si existe; si no, procedural (fallback) ---
  protected drawFloor(): void {
    if (this.drawTilesetFloor()) return;
    const g = this.add.graphics().setDepth(-10000);
    for (let x = 0; x < this.grid.w; x++) {
      for (let y = 0; y < this.grid.h; y++) {
        const { sx, sy } = this.iso(x, y);
        P.paintTile(g, sx, sy, this.tileKind(x, y), x, y);
      }
    }
    this.drawGroundDetails();
  }

  /** Detalle ambiental sembrado sobre el suelo (escombros, charco, marcas de tiza). */
  private drawGroundDetails(): void {
    const g = this.add.graphics().setDepth(-9990);
    const cen = (tx: number, ty: number) => { const p = this.iso(tx, ty); return { cx: p.sx, cy: p.sy + TILE_H / 2 }; };
    // charco (con reflejo de cielo)
    { const { cx, cy } = cen(4.4, 7.4);
      g.fillStyle(0x3a4048, 0.32); g.fillEllipse(cx, cy, 46, 22);
      g.fillStyle(0x8aa6b6, 0.28); g.fillEllipse(cx - 7, cy - 3, 22, 9);
      g.lineStyle(1, 0x2b3036, 0.25); g.strokeEllipse(cx, cy, 46, 22); }
    // montones de escombro
    const rubble = (tx: number, ty: number) => { const { cx, cy } = cen(tx, ty);
      g.fillStyle(0x000000, 0.12); g.fillEllipse(cx, cy + 3, 34, 12);
      for (const [dx, dy, r, c] of [[-8, 0, 5, 0x9a8f7c], [4, -3, 6, 0x8a8578], [12, 2, 4, 0xa89a7e], [-2, 3, 4, 0x7d7466], [8, 4, 3, 0x9a5a3a]] as [number, number, number, number][]) {
        g.fillStyle(c, 1); g.fillCircle(cx + dx, cy + dy, r);
      } };
    rubble(9, 6.2); rubble(1.6, 7.2);
    // marca de tiza (flecha de replanteo)
    { const { cx, cy } = cen(5.4, 6.4);
      g.lineStyle(2, 0xf4ead0, 0.6);
      g.beginPath(); g.moveTo(cx - 16, cy + 6); g.lineTo(cx + 12, cy - 6); g.strokePath();
      g.beginPath(); g.moveTo(cx + 12, cy - 6); g.lineTo(cx + 4, cy - 8); g.moveTo(cx + 12, cy - 6); g.lineTo(cx + 10, cy + 2); g.strokePath(); }
    // cruz de replanteo + mancha de aceite
    { const { cx, cy } = cen(3.4, 5.4); g.lineStyle(2, 0xf4ead0, 0.5);
      g.beginPath(); g.moveTo(cx - 8, cy); g.lineTo(cx + 8, cy); g.moveTo(cx, cy - 5); g.lineTo(cx, cy + 5); g.strokePath(); }
    { const { cx, cy } = cen(6.6, 4.4); g.fillStyle(0x2b2620, 0.3); g.fillEllipse(cx, cy, 24, 12); g.fillStyle(0x2b2620, 0.2); g.fillEllipse(cx + 8, cy + 4, 10, 6); }
  }

  /** Pinta el suelo con el tileset real. Devuelve false si no está disponible. */
  private drawTilesetFloor(): boolean {
    const gr = this.map.ground;
    if (!gr || !useRealAssets() || !this.textures.exists(GROUND_TEX)) return false;
    for (let y = 0; y < gr.h; y++) {
      for (let x = 0; x < gr.w; x++) {
        const gid = gr.data[y * gr.w + x];
        if (!gid) continue; // 0 = celda vacía
        const { sx, sy } = this.iso(x, y);
        this.add.image(sx, sy, GROUND_TEX, gid - gr.firstgid).setOrigin(0.5, 0).setDepth(-10000);
      }
    }
    return true;
  }

  private tileKind(x: number, y: number): P.TileKind {
    if (y < 3) return "concrete";              // zona de trabajo (forjado)
    if (x <= 2 && y >= 6) return "planks";     // acceso de tablas
    if (x >= 7 && y <= 5) return "sand";       // acopio de materiales
    if (Math.abs(x - y) < 1.15) return "path"; // pasillo de circulación (compactado)
    return "dirt";
  }

  // --- decorado desde la capa DECOR del mapa ---
  protected buildDecor(): void {
    this.decorObjs.forEach((d) => d.destroy());
    this.decorObjs = [];
    for (const d of this.map.decor) {
      const obj = makeVisual(this, d.visual);
      this.addProp(obj, d.pos.x, d.pos.y);
      this.decorObjs.push(obj);
    }
  }

  // --- cámara: seguir a EPI, con límites del recinto ---
  protected setupCamera(): void {
    const cam = this.cameras.main;
    const pts = [this.iso(0, 0), this.iso(this.grid.w - 1, 0), this.iso(0, this.grid.h - 1), this.iso(this.grid.w - 1, this.grid.h - 1)];
    const xs = pts.map((p) => p.sx), ys = pts.map((p) => p.sy);
    const minX = Math.min(...xs) - TILE_W, maxX = Math.max(...xs) + TILE_W;
    const minY = Math.min(...ys) - 280, maxY = Math.max(...ys) + TILE_H * 2.5;
    cam.setBounds(minX, minY, maxX - minX, maxY - minY);
    cam.setZoom(1.25);
    cam.startFollow(this.player.container, true, 0.12, 0.12);
    cam.setDeadzone(160, 110);
  }
}
